import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BasicInfoStep = ({ formData, updateFormData, onNext }) => {
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData?.phone && !/^[0-9\s\-\+\(\)]+$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    if (!formData?.agreeToEligibility) {
      newErrors.agreeToEligibility = 'You must confirm you meet the eligibility requirements';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    updateFormData({ [name]: type === 'checkbox' ? checked : value });
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  const getPasswordStrength = () => {
    const password = formData?.password || '';
    if (password?.length === 0) return { strength: 0, label: '', color: '' };
    if (password?.length < 8) return { strength: 25, label: 'Weak', color: 'bg-error' };
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(password)) return { strength: 50, label: 'Fair', color: 'bg-warning' };
    if (password?.length >= 12) return { strength: 100, label: 'Strong', color: 'bg-success' };
    return { strength: 75, label: 'Good', color: 'bg-primary' };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">Basic Information</h2>
        <p className="font-body text-sm text-muted-foreground">Let's start with your account details</p>
      </div>

      <Input
        type="text"
        name="fullName"
        label="Full Name"
        placeholder="John Doe"
        value={formData?.fullName}
        onChange={handleChange}
        error={errors?.fullName}
        required
      />

      <Input
        type="email"
        name="email"
        label="Email Address"
        placeholder="you@example.com"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
      />

      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          name="password"
          label="Password"
          placeholder="Create a strong password"
          value={formData?.password}
          onChange={handleChange}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} strokeWidth={2} />
        </button>
        {formData?.password && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="font-caption text-xs text-muted-foreground">Password strength:</span>
              <span className={`font-caption text-xs font-semibold ${passwordStrength?.strength >= 75 ? 'text-success' : 'text-warning'}`}>
                {passwordStrength?.label}
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${passwordStrength?.color}`}
                style={{ width: `${passwordStrength?.strength}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="relative">
        <Input
          type={showConfirmPassword ? 'text' : 'password'}
          name="confirmPassword"
          label="Confirm Password"
          placeholder="Re-enter your password"
          value={formData?.confirmPassword}
          onChange={handleChange}
          error={errors?.confirmPassword}
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
        >
          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} strokeWidth={2} />
        </button>
      </div>

      <Input
        type="tel"
        name="phone"
        label="Phone Number (Optional)"
        placeholder="555-123-4567"
        value={formData?.phone}
        onChange={handleChange}
        error={errors?.phone}
      />

      <div>
        <label className="font-body text-sm font-medium text-foreground mb-3 block">
          Account Type <span className="text-destructive">*</span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => updateFormData({ accountType: 'owner' })}
            className={`p-4 rounded-lg border-2 transition-smooth text-left ${
              formData?.accountType === 'owner' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-center space-x-3 mb-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                formData?.accountType === 'owner' ? 'bg-primary' : 'bg-muted'
              }`}>
                <Icon name="Truck" size={20} color={formData?.accountType === 'owner' ? 'white' : 'var(--color-muted-foreground)'} strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="font-body font-semibold text-foreground">Owner</p>
              </div>
            </div>
            <p className="font-caption text-xs text-muted-foreground">List and rent out your trailers</p>
          </button>

          <button
            type="button"
            onClick={() => updateFormData({ accountType: 'renter' })}
            className={`p-4 rounded-lg border-2 transition-smooth text-left ${
              formData?.accountType === 'renter' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-center space-x-3 mb-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                formData?.accountType === 'renter' ? 'bg-primary' : 'bg-muted'
              }`}>
                <Icon name="User" size={20} color={formData?.accountType === 'renter' ? 'white' : 'var(--color-muted-foreground)'} strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="font-body font-semibold text-foreground">Renter</p>
              </div>
            </div>
            <p className="font-caption text-xs text-muted-foreground">Find and rent trailers for your needs</p>
          </button>
        </div>
      </div>

      <div>
        <label className="flex items-start space-x-3 cursor-pointer">
          <div className="relative mt-1 flex-shrink-0">
            <input
              type="checkbox"
              name="agreeToTerms"
              checked={formData?.agreeToTerms}
              onChange={handleChange}
              className="sr-only"
            />
            <div className={`h-4 w-4 rounded border-2 border-[#FF6B35] flex items-center justify-center transition-colors ${formData?.agreeToTerms ? 'bg-[#FF6B35]' : 'bg-white'}`}>
              {formData?.agreeToTerms && (
                <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
          </div>
          <span className="font-body text-sm text-foreground">
            I agree to the{' '}
            <a href="#" className="text-primary hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-primary hover:underline">Privacy Policy</a>
          </span>
        </label>
        {errors?.agreeToTerms && (
          <p className="mt-2 text-sm text-destructive">{errors?.agreeToTerms}</p>
        )}
      </div>

      <div>
        <label className="flex items-start space-x-3 cursor-pointer">
          <div className="relative mt-1 flex-shrink-0">
            <input
              type="checkbox"
              name="agreeToEligibility"
              checked={formData?.agreeToEligibility}
              onChange={handleChange}
              className="sr-only"
            />
            <div className={`h-4 w-4 rounded border-2 border-[#FF6B35] flex items-center justify-center transition-colors ${formData?.agreeToEligibility ? 'bg-[#FF6B35]' : 'bg-white'}`}>
              {formData?.agreeToEligibility && (
                <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
          </div>
          <span className="font-body text-sm text-foreground">
            I confirm that I am at least 18 years old and legally able to enter into contracts
          </span>
        </label>
        {errors?.agreeToEligibility && (
          <p className="mt-2 text-sm text-destructive">{errors?.agreeToEligibility}</p>
        )}
      </div>

      <Button type="submit" fullWidth iconName="ArrowRight" iconPosition="right">
        Continue to Document Verification
      </Button>
    </form>
  );
};

export default BasicInfoStep;